// App.js
import { useEffect, useRef } from "react";
import Navigation from "@/components/Navigation";
import StepRenderer from "@/components/StepRenderer";
import Button from "@/components/ui/Button";
import useQuizStore from "@/store/useQuizStore";
import { TOTAL_STEPS } from "@/data/steps";

const PRELOAD_MAP = {
  9: [
    "/src/public/img/bottle.avif",
    "/src/public/img/illustrations/probiotic-strains.avif",
    "/src/public/img/illustrations/prebiotic-fibers.avif",
    "/src/public/img/illustrations/postbiotic-enzymes.avif",
  ],
  18: [
    "/src/public/img/naori-metabolism.svg",
    "/src/public/img/illustrations/hunger.avif",
    "/src/public/img/illustrations/fat-storage.avif",
    "/src/public/img/illustrations/brain-fog.avif",
    "/src/public/img/illustrations/bad-bacteria.avif",
    "/src/public/img/illustrations/bloating.avif",
  ],
  19: [
    "/src/public/img/product.avif",
    "/src/public/img/illustrations/gut-composition.avif",
    "/src/public/img/illustrations/prebiotics-probiotics.avif",
    "/src/public/img/illustrations/delayed-release.avif",
    "/src/public/img/illustrations/freeze-dried.avif",
    "/src/public/img/illustrations/dark-glass-bottle.avif",
  ],
  20: [
    "/src/public/img/hand.avif",
    "/src/public/img/illustrations/two-capsules.avif",
    "/src/public/img/illustrations/healthy-gut.avif",
    "/src/public/img/benefits.svg",
  ],
  21: ["/src/public/img/before.avif", "/src/public/img/after.avif"],
  22: [
    "/src/public/img/testimonials/testimonials1.avif",
    "/src/public/img/testimonials/testimonials2.avif",
    "/src/public/img/testimonials/testimonials3.avif",
    "/src/public/img/testimonials/testimonials4.avif",
    "/src/public/img/covered.svg",
  ],
};

const WARMUP_FLAG = "__adaptiveQuizCheckoutWarmupDone";

function ensureHeadLink({ rel, href, as, crossOrigin }) {
  if (!document?.head || !href) return;

  const existing = Array.from(document.head.querySelectorAll("link")).find(
    (node) =>
      node.rel === rel &&
      node.href === href &&
      (as ? node.getAttribute("as") === as : true),
  );

  if (existing) return;

  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;

  if (as) {
    link.as = as;
  }

  if (crossOrigin) {
    link.crossOrigin = crossOrigin;
  }

  document.head.appendChild(link);
}

function warmupCheckoutNavigation(rawUrl) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!rawUrl) return;

  let targetUrl;
  try {
    targetUrl = new URL(rawUrl, window.location.href);
  } catch {
    return;
  }

  if (!["http:", "https:"].includes(targetUrl.protocol)) return;

  const origin = targetUrl.origin;
  const sameOrigin = origin === window.location.origin;

  ensureHeadLink({ rel: "dns-prefetch", href: origin });
  ensureHeadLink({
    rel: "preconnect",
    href: origin,
    crossOrigin: sameOrigin ? undefined : "anonymous",
  });

  if (!sameOrigin) return;

  const connection = navigator.connection || navigator.mozConnection;
  if (connection?.saveData) return;

  ensureHeadLink({
    rel: "prefetch",
    href: targetUrl.href,
    as: "document",
  });
}

function App() {
  const { currentStep, isValid, goToNext, goToPrevious } = useQuizStore();
  const warmupStartedRef = useRef(false);

  // Only for development !!!
  useEffect(() => {
    if (window.app_env === "dev") {
      const handleKeyDown = (e) => {
        // Ctrl/Cmd + Arrow Right = next step
        if ((e.ctrlKey || e.metaKey) && e.key === "ArrowRight") {
          e.preventDefault();
          const { currentStep, setCurrentStep } = useQuizStore.getState();
          setCurrentStep(Math.min(currentStep + 1, TOTAL_STEPS));
        }
        // Ctrl/Cmd + Arrow Left = previous step
        if ((e.ctrlKey || e.metaKey) && e.key === "ArrowLeft") {
          e.preventDefault();
          const { currentStep, setCurrentStep } = useQuizStore.getState();
          setCurrentStep(Math.max(currentStep - 1, 1));
        }
        // Ctrl/Cmd + G = prompt for step number
        if ((e.ctrlKey || e.metaKey) && e.key === "g") {
          e.preventDefault();
          const step = prompt(`Go to step (1-${TOTAL_STEPS}):`);
          if (step) {
            const num = parseInt(step);
            const { setCurrentStep } = useQuizStore.getState();
            if (num >= 1 && num <= TOTAL_STEPS) {
              setCurrentStep(num);
            }
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, []);

  const { getCurrentStepData } = useQuizStore.getState();
  const currentStepData = getCurrentStepData();

  useEffect(() => {
    const imagesToLoad = PRELOAD_MAP[currentStep];

    if (imagesToLoad && imagesToLoad.length > 0) {
      const loadTask = () => {
        imagesToLoad.forEach((path) => {
          const img = new Image();
          img.src = `${window.template_root}${path}`;
        });
      };

      if ("requestIdleCallback" in window) {
        requestIdleCallback(loadTask);
      } else {
        setTimeout(loadTask, 200);
      }
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStep < 16) return;
    if (warmupStartedRef.current) return;

    const ctaLink = window.cta_link;
    if (!ctaLink) return;

    if (window[WARMUP_FLAG]) {
      warmupStartedRef.current = true;
      return;
    }

    warmupStartedRef.current = true;
    window[WARMUP_FLAG] = true;
    warmupCheckoutNavigation(ctaLink);
  }, [currentStep]);

  // Determine if we're in summary section (steps 16-23)
  const isSummary = currentStep >= 16;

  // Calculate progress step for display
  const getProgressStep = () => {
    if (isSummary) return currentStep;
    if (currentStep === 14) return 13;
    if (currentStep === 15) return 14;
    return currentStep;
  };

  // Determine if back button should be visible
  const showBackButton =
    currentStep > 1 &&
    currentStep !== 14 &&
    currentStep !== 15 &&
    currentStep !== 16;

  // Determine if we need the fixed bottom button
  const needsFixedButton =
    currentStepData?.type === "multiselect" ||
    currentStepData?.type === "info" ||
    currentStepData?.type === "summary";

  return (
    <div className="absolute inset-0 overflow-hidden hide-scrollbar flex flex-col bg-gradient-to-b from-[#DFF3E4] to-[#E7F1ED] m-2 md:m-[15px] rounded-[18px] md:rounded-[22px] w-[calc(100%-16px)] md:w-[calc(100%-30px)] h-[calc(100%-16px)] md:h-[calc(100%-30px)]">
      <Navigation
        currentStep={getProgressStep()}
        showBackButton={showBackButton}
        onBack={goToPrevious}
        isSummary={isSummary}
      />

      <main className="flex-1 px-4 py-5 md:py-8 max-w-[520px] mx-auto w-full overflow-y-auto hide-scrollbar">
        <StepRenderer step={currentStepData} stepNumber={currentStep} />
      </main>

      {needsFixedButton && (
        <Button onClick={goToNext} disabled={!isValid} fixed>
          Continue
        </Button>
      )}
    </div>
  );
}

export default App;
